import { store } from '@redux/store';
import { setModalStep } from '../redux/slices';
import {
  verifyCode,
  sendVerificationCode
} from '../services/phoneVerificationService';
import { getPhoneNextVerificationMethodStep } from '../services/verificationMethodService';
import { getStoredUserData } from '../services/userService';
import VerificationCodeInput from './verificationCodeInput';
import UserStatus from '../enums/userStatus';
import ModalStep from '../enums/modalStep';
import Phone from './phone';

/**
 * Represents the phone verification process, including rendering the UI,
 * handling user interactions, and managing state transitions for verifying a user's phone.
 */
export default class PhoneVerification {
  verificationMode = false;

  resendCodeButton$ = null;

  currentValidPhoneNumber = null;

  phoneValidSubscription$ = null;

  phoneOnBlurSubscription$ = null;

  /**
   * Initializes a new instance of the class.
   *
   * @param {Object} rootComponent - The root component used for managing the UI interactions and subscriptions.
   */
  constructor(rootComponent) {
    this.rootComponent = rootComponent;
    this.phone = new Phone();
    this.phone.placeholder = '(_ _ _) _ _ _-_ _ _ _';
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate);
    this.codeInput = new VerificationCodeInput(
      this.getValidCodeCallback,
      this.resendCode,
      this.onCountdownEnd,
      this.handleIncompleteCode
    );
    this.nextStep = getPhoneNextVerificationMethodStep(
      this.rootComponent.verificationMethods
    );
  }

  /**
   * Returns the DOM element that contains the verification code input.
   * @returns {Element}
   */
  static getCodeInputContainer() {
    return VerificationCodeInput.getContainer();
  }

  /**
   * Shows the verification code input container.
   */
  static showCodeInput() {
    const codeInput = PhoneVerification.getCodeInputContainer();
    codeInput.hidden = false;

    const firstInput = codeInput.querySelector(
      '[data-self-guided-tour-code-input]'
    );
    if (firstInput) {
      firstInput.focus();
    }
  }

  /**
   * Hides the verification code input container.
   */
  static hideCodeInput() {
    const codeInput = PhoneVerification.getCodeInputContainer();
    codeInput.hidden = true;
  }

  /**
   * Handles the event when the countdown ends for the verification code input.
   */
  onCountdownEnd = () => {
    this.rootComponent.setPrimaryButtonDisabled(true);
  };

  /**
   * Handles the event when the verification code input is valid i.e all inputs are filled.
   * @param {String} code - The verification code entered by the user.
   */
  getValidCodeCallback = code => {
    // `code` is null if any of the inputs are empty
    this.rootComponent.setPrimaryButtonDisabled(!code);
    if (code) {
      VerificationCodeInput.hideError();
    }
  };

  /**
   * Handles the DOM update event by binding events to the phone and code input components.
   */
  onDomUpdate = event => {
    if (event.detail?.step === ModalStep.PhoneVerification) {
      this.phone.bindEvents();
      this.codeInput.bindEvents();
      this.setUserPhoneInInput();
      this.phoneValidSubscription$ = this.phone.isValid$.subscribe(
        this.handleValidPhone
      );
      this.phoneOnBlurSubscription$ = this.phone
        .getOnChangeEvent$()
        .subscribe(() => {
          this.phone.validate();
        });

      if (this.phone.getValue() && this.phone.validate()) {
        this.handleValidPhone({ valid: true });
      }
    } else {
      this.codeInput.unbindEvents();
      this.phoneValidSubscription$?.unsubscribe();
      this.phoneOnBlurSubscription$?.unsubscribe();
      this.verificationMode = false;
    }
  };

  /**
   * Handles the validation state of a phone number input.
   *
   * This function logs the validity status of the phone number and enables
   * or disables the primary button based on the validity.
   *
   * @param {boolean} valid - A boolean representing whether the phone number is valid.
   */
  handleValidPhone = ({ valid }) => {
    if (!this.verificationMode) {
      this.rootComponent.setPrimaryButtonDisabled(!valid);
    } else {
      VerificationCodeInput.setResendCodeButtonDisabled(!valid);
    }
  };

  /**
   * Handles the action of resending the verification code.
   */
  resendCode = async () => {
    const validPhoneNumber = this.phone.validate();
    if (!validPhoneNumber) {
      return;
    }
    this.rootComponent.setPrimaryButtonDisabled(true);
    this.rootComponent.removeNotification();
    this.codeInput.stopCountdown();
    // set the phone number to the current valid phone number
    // we assume that this action can be performed only if the phone number is valid
    this.currentValidPhoneNumber = this.phone.getValue();
    await sendVerificationCode(this.currentValidPhoneNumber);
    this.rootComponent.showNotification(
      'A new verification code has been sent.'
    );
    this.codeInput.startCountdown();
  };

  handleIncompleteCode = () => {
    this.rootComponent.setPrimaryButtonDisabled(true);
  };

  setUserPhoneInInput() {
    const user = getStoredUserData();
    this.phone.value = user.phoneNumber;
  }

  /**
   * Handles the primary action for the tour configuration.
   * Dispatches an action to update the modal step in the store with the next step.
   *
   */
  async handlePrimaryAction() {
    if (!this.verificationMode) {
      this.currentValidPhoneNumber = this.phone.getValue();
      try {
        await sendVerificationCode(this.currentValidPhoneNumber);
        PhoneVerification.showCodeInput();
        this.rootComponent.setPrimaryButtonDisabled(true);
        this.rootComponent.showNotification(
          'A verification code has been sent.'
        );
        this.rootComponent.footer.setPrimaryButtonText('Verify');
        this.codeInput.startCountdown();
        this.verificationMode = true;
      } catch {
        this.phone.validate();
        this.rootComponent.showNotification(
          "The provided number doesn't appear to be a mobile phone number."
        );
      }
    } else {
      const code = VerificationCodeInput.getCode();
      try {
        await verifyCode(this.currentValidPhoneNumber, code);
        this.codeInput.stopCountdown();
        VerificationCodeInput.hideError();
        const userData = { ...getStoredUserData() };
        const nextUserData = {
          ...userData,
          phoneNumber: this.currentValidPhoneNumber
        };
        this.rootComponent.onUserChange$.next(nextUserData);
        store.dispatch(setModalStep({ modalStep: this.nextStep }));
      } catch {
        VerificationCodeInput.showError();
      }
    }
  }

  /**
   * Handles the secondary action by dispatching an action to update the modal step
   * to the previous step in the application's state.
   *
   */
  handleSecondaryAction() {
    const { name } = this.rootComponent.userStatus;
    const userStatus = UserStatus.fromKey(name);
    if (userStatus === UserStatus.NotFound) {
      store.dispatch(setModalStep({ modalStep: ModalStep.SignUp }));
      return;
    }
    store.dispatch(setModalStep({ modalStep: ModalStep.SignIn }));
  }

  render() {
    return `
      <div class="nhs-c-self-guided-left-panel ${this.class}">
        <div>
          <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Verify mobile phone</h1>

          <div class="nhs-c-notification__placeholder" data-self-guided-tour-notifications></div>

          <p class="nhs-c-label--black-900-body-1-medium-regular">Verify your mobile phone: Tap "Send code" or enter a new number.</p>
        </div>
        <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600">
          ${this.phone.getHtml()}
          ${this.codeInput.render()}
        </div>
      </div>
    `;
  }
}
